import { PATHS } from '../../routes/paths';
import type { ChecklistStep } from './steps';

export type RowAction = {
  /** Where an incomplete step sends you. `null` when there is nowhere useful to go. */
  to: string | null;
  /** Waiting on the step above it, rather than simply not started. */
  blocked: boolean;
};

/** Where a row sits in the run: finished, the one to do now, or further down the list. */
export type StepTurn = 'done' | 'next' | 'later';

/**
 * The step the checklist is on: the FIRST unfinished one, never the one after the last
 * finished. The distinction is the whole point in the reachable out-of-order state — a team
 * holding a portfolio built from a channel outside `APPROVED_CHANNEL_KEYS` has step 2 done and
 * step 1 idle, and the card must ask for step 1 rather than skipping past it.
 */
export function turnStep(steps: readonly ChecklistStep[]): ChecklistStep | null {
  // `find`, not an `every` over a slice: `[].every()` is vacuously true, which would promote
  // the first row of an empty board to `next`.
  return steps.find((candidate) => candidate.status !== 'done') ?? null;
}

export function stepTurn(step: ChecklistStep, steps: readonly ChecklistStep[]): StepTurn {
  if (step.status === 'done') return 'done';
  return turnStep(steps)?.id === step.id ? 'next' : 'later';
}

/**
 * Whether a row has to wait for the one above it.
 *
 * `add-content` is gated on the REAL dependency rather than on the run order, and that is not a
 * softening — it is what keeps the card usable for a team whose step 1 can never complete.
 * `APPROVED_CHANNEL_KEYS` omits LinkedIn/Snapchat/Adform/DV360 and `IntegrationCard` never locks
 * an already-connected channel, so such a team runs portfolios with step 1 idle forever.
 */
function isBlocked(step: ChecklistStep, steps: readonly ChecklistStep[], portfolioId: number | null): boolean {
  if (step.id === 'add-content') return portfolioId === null;
  return stepTurn(step, steps) === 'later';
}

/**
 * Where a row goes, and whether it can go anywhere yet.
 *
 * The checklist runs in order: a row waits until the one above it is done. Everything the panel
 * draws as waiting — the dashed marker, the missing link, the reason line — comes from
 * `blocked`, so it is decided here once rather than re-derived per visual.
 */
export function rowAction(
  step: ChecklistStep,
  steps: readonly ChecklistStep[],
  portfolioId: number | null,
): RowAction {
  if (step.status === 'done') return { to: null, blocked: false };
  if (isBlocked(step, steps, portfolioId)) return { to: null, blocked: true };

  if (step.id === 'connect-channel') return { to: PATHS.APP.SETTINGS_INTEGRATIONS, blocked: false };
  if (step.id === 'create-portfolio') return { to: PATHS.APP.PORTFOLIOS, blocked: false };

  // `portfolioId` still guards the URL, not just the gate: `isBlocked` already proved it
  // non-null, and interpolating a null here would route to `/portfolios/null`.
  return portfolioId === null
    ? { to: null, blocked: true }
    : { to: `/portfolios/${portfolioId}?activeTab=content&activeContentTab=content`, blocked: false };
}
